import * as React from 'react';
import { NumberField as BaseNumberField } from '@base-ui/react/number-field';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import OutlinedInput from '@mui/material/OutlinedInput';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';



export default function NumberSpinner({
    id: idProp,
    label,
    error,
    size = 'medium', // 'xs' | 'small' | 'medium';
    ...other
}) {
    let id = React.useId();
    if (idProp) id = idProp;

    const isXs = size === 'xs';

    // MUI components only support small|medium for FormControl, and Button supports small|medium|large.
    // So map xs -> small and then shrink with sx.
    const muiSize = isXs ? 'small' : size;

    // Tokens for the xs look
    const xs = {
        controlHeight: 30,
        buttonWidth: 30,
        buttonPx: 0,          // tighter than small
        inputPx: 1,           // theme spacing units
        inputPy: 0.30,
        labelFontSize: '0.8rem',
        labelMb: 0.30,
        scrubIconFontSize: 16,
        stepIconFontSize: 20,
    };

    return (
        <BaseNumberField.Root
            {...other}
            render={(props, state) => (
                <FormControl
                    size={muiSize}
                    ref={props.ref}
                    disabled={state.disabled}
                    required={state.required}
                    error={error}
                    variant="outlined"
                    sx={{
                        // shared button styling
                        '& .MuiButton-root': {
                            borderColor: 'divider',
                            minWidth: 0,
                            bgcolor: 'action.hover',
                            '&:not(.Mui-disabled)': { color: 'text.primary' },
                        },

                        // xs: tighten overall vertical rhythm
                        ...(isXs && {
                            '& .MuiButton-root': {
                                minHeight: xs.controlHeight,
                                height: xs.controlHeight,
                                width: xs.buttonWidth,
                                minWidth: xs.buttonWidth,
                                paddingLeft: xs.buttonPx,
                                paddingRight: xs.buttonPx,
                            },
                        }),
                    }}
                >
                    {props.children}
                </FormControl>
            )}
        >
            {label && (
                <BaseNumberField.ScrubArea
                    render={<Box component="span" sx={{ userSelect: 'none', width: 'max-content' }} />}
                >
                    <FormLabel
                        htmlFor={id}
                        sx={{
                            display: 'inline-block',
                            cursor: 'ew-resize',
                            fontSize: isXs ? xs.labelFontSize : '0.875rem',
                            color: 'text.primary',
                            fontWeight: 500,
                            lineHeight: 1.5,
                            mb: isXs ? xs.labelMb : 0.5,
                        }}
                    >
                        {label}
                    </FormLabel>

                    <BaseNumberField.ScrubAreaCursor>
                        <OpenInFullIcon
                            // keep MUI's prop valid, then override size with sx for xs
                            fontSize="small"
                            sx={{
                                transform: 'translateY(12.5%) rotate(45deg)',
                                ...(isXs && { fontSize: xs.scrubIconFontSize }),
                            }}
                        />
                    </BaseNumberField.ScrubAreaCursor>
                </BaseNumberField.ScrubArea>
            )}

            <Box
                sx={{
                    display: 'flex',
                    ...(isXs && { height: xs.controlHeight, alignItems: 'stretch' }),
                }}
            >
                <BaseNumberField.Decrement
                    render={
                        <Button
                            variant="outlined"
                            aria-label="Decrease"
                            size={muiSize}
                            sx={{
                                borderTopRightRadius: 0,
                                borderBottomRightRadius: 0,
                                borderRight: '0px',
                                '&.Mui-disabled': { borderRight: '0px' },
                            }}
                        />
                    }
                >
                    <RemoveIcon
                        fontSize={muiSize}
                        sx={isXs ? { fontSize: xs.stepIconFontSize } : undefined}
                    />
                </BaseNumberField.Decrement>

                <BaseNumberField.Input
                    id={id}
                    render={(props, state) => (
                        <OutlinedInput
                            inputRef={props.ref}
                            value={state.inputValue}
                            onBlur={props.onBlur}
                            onChange={props.onChange}
                            onKeyUp={props.onKeyUp}
                            onKeyDown={props.onKeyDown}
                            onFocus={props.onFocus}
                            slotProps={{
                                input: {
                                    ...props,
                                    // "size" here is the HTML input attribute (characters), not MUI size.
                                    size:
                                        Math.max(
                                            (other.min?.toString() || '').length,
                                            state.inputValue.length || 1,
                                        ) + 1,
                                    sx: {
                                        textAlign: 'center',
                                    },
                                },
                            }}
                            sx={{
                                pr: 0,
                                borderRadius: 0,
                                flex: 1,

                                ...(isXs && {
                                    height: xs.controlHeight,
                                    '& .MuiOutlinedInput-input': {
                                        paddingLeft: xs.inputPx,
                                        paddingRight: xs.inputPx,
                                        paddingTop: xs.inputPy,
                                        paddingBottom: xs.inputPy,
                                    },
                                }),
                            }}
                        />
                    )}
                />

                <BaseNumberField.Increment
                    render={
                        <Button
                            variant="outlined"
                            aria-label="Increase"
                            size={muiSize}
                            sx={{
                                borderTopLeftRadius: 0,
                                borderBottomLeftRadius: 0,
                                borderLeft: '0px',
                                '&.Mui-disabled': { borderLeft: '0px' },
                            }}
                        />
                    }
                >
                    <AddIcon
                        fontSize={muiSize}
                        sx={isXs ? { fontSize: xs.stepIconFontSize } : undefined}
                    />
                </BaseNumberField.Increment>
            </Box>
        </BaseNumberField.Root>
    );
}
